// src/components/appointments/CancelAppointmentModal.tsx
import React, { useState } from "react";
import { toast } from "react-toastify";
import { AppointmentAPI } from "../../services/appointment.service";
import { actionErrorMessage, isBackendUnreachable, isPermissionDenied } from "../../utils/network";

interface Props {
  isOpen: boolean;
  appointmentId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const CancelAppointmentModal: React.FC<Props> = ({
  isOpen,
  appointmentId,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCancel = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      toast.error("Cancellation reason is required");
      return;
    }
    setLoading(true);
    try {
      await AppointmentAPI.cancel(appointmentId, trimmed);
      toast.success("Appointment cancelled");
      setReason("");
      onSuccess();
      onClose();
    } catch (e: any) {
      // A 403 is the server answering, so it must not read as "you are offline".
      if (isPermissionDenied(e)) {
        toast.error(actionErrorMessage(e, "Failed to cancel appointment"));
      } else if (isBackendUnreachable(e)) {
        toast.error("You are offline — cancelling appointments requires a connection");
      } else {
        toast.error(e.message || "Failed to cancel appointment");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800">Cancel Appointment</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl font-bold">×</button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-slate-500">
            Please provide a reason for cancellation. This is required and will be saved.
          </p>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Cancellation Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. Patient request, Doctor unavailable…"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm hover:bg-slate-50"
            >
              Go Back
            </button>
            <button
              onClick={handleCancel}
              disabled={loading || !reason.trim()}
              className="flex-1 bg-red-500 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-red-600 disabled:opacity-40"
            >
              {loading ? "Cancelling…" : "Cancel Appointment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};